import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaTimes, FaImage } from 'react-icons/fa';
import { 
  subscribeToNotices, 
  addNotice as addNoticeToFirebase, 
  updateNotice as updateNoticeInFirebase, 
  deleteNotice as deleteNoticeFromFirebase 
} from '../firebase/noticeService';

const NoticeContainer = styled.div`
  padding-top: ${props => props.theme.sizes.headerHeight};
  max-width: ${props => props.theme.sizes.maxWidth};
  margin: 0 auto;
  padding: 120px 20px 60px;

  @media (max-width: 768px) {
    padding: 100px 15px 40px;
  }
`;

const Title = styled.h1`
  text-align: center;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 40px;
  font-size: 2.8rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 30px;
  }
`;

const Description = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.lightText};
  margin-bottom: 60px;
  line-height: 1.6;
  font-size: 1.1rem;

  .mobile-break {
    display: none;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 40px;
    
    .mobile-break {
      display: block;
    }
  }
`;

// 고정 공지사항 스타일
const FixedNoticeContainer = styled.div`
  margin-bottom: 40px;
`;

const FixedNoticeItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: ${props => props.theme.shadows.medium};
  border: 2px solid ${props => props.theme.colors.primary};
  position: relative;
  margin-bottom: 20px;

  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.2rem;
    font-weight: bold;
  }

  .date {
    color: ${props => props.theme.colors.lightText};
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 15px;
  }

  .schedule-title {
    color: ${props => props.theme.colors.primary};
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .schedule-item {
    color: ${props => props.theme.colors.text};
    line-height: 1.8;
    font-size: 1rem;
    margin-bottom: 8px;
  }

  .notice-text {
    color: ${props => props.theme.colors.text};
    font-size: 1rem;
    line-height: 1.6;
    margin: 15px 0;
    white-space: pre-wrap;
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 10px;

    h3 {
      font-size: 1.2rem;
      margin-bottom: 12px;
    }

    .date {
      font-size: 13px;
      margin-bottom: 12px;
    }

    .schedule-title {
      font-size: 1rem;
    }

    .schedule-item {
      font-size: 0.9rem;
    }

    .notice-text {
      font-size: 0.95rem;
    }
  }
`;

const AdminControls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const AdminButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 16px;
  }
`;

const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const NoticeItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: ${props => props.theme.shadows.medium};
  border: ${props => {
    if (props.important || props.special) return `2px solid ${props.theme.colors.primary}`;
    return 'none';
  }};
  position: relative;

  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.2rem;
  }

  .date {
    color: ${props => props.theme.colors.lightText};
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 15px;
  }

  p {
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
    font-size: 1rem;
    white-space: pre-wrap;
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 10px;

    h3 {
      font-size: 1.2rem;
      margin-bottom: 12px;
    }

    .date {
      font-size: 13px;
      margin-bottom: 12px;
    }

    p {
      font-size: 0.95rem;
    }
  }
`;

const ItemControls = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    gap: 8px;
  }
`;

const ControlButton = styled.button`
  background: ${props => props.danger ? props.theme.colors.error : props.theme.colors.secondary};
  color: white;
  border: none;
  padding: 5px 8px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.large};
  position: relative;

  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 20px;
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 20px;
    width: 95%;
    max-height: 90vh;
    border-radius: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: ${props => props.theme.colors.lightText};

  &:hover {
    color: ${props => props.theme.colors.text};
  }

  @media (max-width: 768px) {
    font-size: 24px;
    top: 10px;
    right: 10px;
  }
`;

const NoticeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;

  input, textarea {
    padding: 12px;
    border: 2px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    font-size: 16px;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }

  .checkbox-group {
    display: flex;
    align-items: center;
    gap: 10px;

    input[type="checkbox"] {
      width: auto;
      padding: 0;
    }
  }

  .image-upload-section {
    border: 2px dashed ${props => props.theme.colors.border};
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    background: #fafafa;

    input[type="file"] {
      display: none;
    }

    .upload-button {
      background: ${props => props.theme.colors.primary};
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      transition: background 0.3s;

      &:hover {
        background: ${props => props.theme.colors.hover};
      }
    }
  }

  .image-preview {
    margin-top: 15px;
    position: relative;
    display: inline-block;

    img {
      max-width: 200px;
      max-height: 150px;
      border-radius: 8px;
      object-fit: cover;
    }

    .remove-image {
      position: absolute;
      top: -8px;
      right: -8px;
      background: ${props => props.theme.colors.error};
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;

      &:hover {
        background: darkred;
      }
    }
  }

  button {
    background: ${props => props.theme.colors.primary};
    color: white;
    border: none;
    padding: 12px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background: ${props => props.theme.colors.hover};
    }
  }

  @media (max-width: 768px) {
    gap: 12px;

    input, textarea {
      padding: 14px;
      font-size: 16px;
    }

    button {
      padding: 14px;
      font-size: 16px;
    }
  }
`;

const Notice = () => {
  const [notices, setNotices] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    important: false,
    image: null,
    imagePreview: null
  });

  // Firebase에서 실시간으로 공지사항 구독
  useEffect(() => {
    let unsubscribe;
    
    try {
      unsubscribe = subscribeToNotices((noticesData) => {
        setNotices(noticesData);
        setLoading(false);
        setError(null);
      });
    } catch (err) {
      console.error('Firebase 연결 오류:', err);
      setError('공지사항을 불러오는데 실패했습니다.');
      setLoading(false);
      
      // Firebase 연결 실패 시 기본 공지사항 표시
      setNotices([
        {
          id: 'default-1',
          title: "당일진료 원하시면 내원접수 해주세요",
          date: "2024-01-15",
          content: "❤️ 당일진료 접수시간\n\n평일 오전: 9:30 ~ 10:00 까지\n평일 오후: 2:00 ~ 5:30 까지\n\n평일 오전에는 수면치료가 있습니다\n간혹 수면치료 없는 날도 있으니 꼭 전화로 확인해 주세요\n\n토요일: 오전9:30 ~ 오후1:00 까지",
          important: true
        },
        {
          id: 'default-2',
          title: "처음 오시는 분 불소도포, 발치, 영유아구강검진도 예약 가능합니다",
          date: "2024-01-15",
          content: "🧡 예약은 전화예약 해주세요\n031-636-3360",
          important: false,
          special: true
        }
      ]);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    const checkAdminStatus = () => {
      const adminStatus = localStorage.getItem('isAdmin');
      setIsAdmin(adminStatus === 'true');
    };

    // 초기 체크
    checkAdminStatus();

    // localStorage 변경 이벤트 리스너
    const handleStorageChange = (e) => {
      if (e.key === 'isAdmin') {
        checkAdminStatus();
      }
    };

    // 주기적 체크 (1초마다)
    const interval = setInterval(checkAdminStatus, 1000);

    // 이벤트 리스너 등록
    window.addEventListener('storage', handleStorageChange);

    // 클린업
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const openAddModal = () => {
    setEditingNotice(null);
    setFormData({ title: '', content: '', important: false, image: null, imagePreview: null });
    setShowModal(true);
  };

  const openEditModal = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      important: notice.important || false,
      image: null,
      imagePreview: notice.image || null
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingNotice(null);
    setFormData({ title: '', content: '', important: false, image: null, imagePreview: null });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 파일 크기 체크 (10MB 제한)
      if (file.size > 10 * 1024 * 1024) {
        alert('파일 크기가 너무 큽니다. 10MB 이하의 파일을 선택해주세요.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        // 이미지 압축 함수 호출
        compressImage(reader.result, (compressedDataUrl) => {
          setFormData({
            ...formData,
            image: file,
            imagePreview: compressedDataUrl
          });
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // 이미지 압축 함수
  const compressImage = (dataUrl, callback) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // 최대 크기 설정 (긴 쪽 기준 800px)
      const maxSize = 800;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = height * (maxSize / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = width * (maxSize / height);
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // 이미지 그리기
      ctx.drawImage(img, 0, 0, width, height);
      
      // 압축 품질 조정하여 1MB 이하로 만들기
      let quality = 0.8;
      let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      
      // 크기가 1MB보다 크면 품질을 낮춰서 재압축
      while (compressedDataUrl.length > 1048576 && quality > 0.1) {
        quality -= 0.1;
        compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      }
      
      // 여전히 크면 더 작은 크기로 조정
      if (compressedDataUrl.length > 1048576) {
        const smallerMaxSize = 600;
        if (width > height) {
          height = height * (smallerMaxSize / width);
          width = smallerMaxSize;
        } else {
          width = width * (smallerMaxSize / height);
          height = smallerMaxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
      }
      
      callback(compressedDataUrl);
    };
    img.src = dataUrl;
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      image: null,
      imagePreview: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const noticeData = {
      title: formData.title,
      content: formData.content,
      important: formData.important,
      image: formData.imagePreview,
      date: new Date().toISOString().split('T')[0]
    };
    
    try {
      if (editingNotice) {
        await updateNoticeInFirebase(editingNotice.id, noticeData);
      } else {
        await addNoticeToFirebase(noticeData);
      }
      closeModal();
    } catch (error) {
      console.error('공지사항 저장 실패:', error);
      alert('공지사항 저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleDelete = async (noticeId) => {
    if (window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) {
      try {
        await deleteNoticeFromFirebase(noticeId);
      } catch (error) {
        console.error('공지사항 삭제 실패:', error);
        alert('공지사항 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  return (
    <NoticeContainer>
      <Title>공지사항</Title>
      <Description>
        서울쥬니어치과의 중요한 <br className="mobile-break" />공지사항을 확인하세요.
      </Description>

      {/* 고정 공지사항 */}
      <FixedNoticeContainer>
        <FixedNoticeItem
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h3>당일진료 원하시면 내원접수 해주세요</h3>
          <div className="schedule-title">
            ❤️ 당일진료 접수시간
          </div>
          <div className="notice-text">
            평일 오전: 9:30 ~ 10:00 까지{'\n'}평일 오후: 2:00 ~ 5:30 까지{'\n'}{'\n'}평일 오전에는 수면치료가 있습니다{'\n'}간혹 수면치료 없는 날도 있으니 꼭 전화로 확인해 주세요{'\n'}{'\n'}토요일: 오전9:30 ~ 오후1:00 까지
          </div>
        </FixedNoticeItem>

        <FixedNoticeItem
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3>처음 오시는 분 불소도포, 발치, 영유아구강검진도 예약 가능합니다</h3>
          <div className="schedule-title">
            🧡 예약은 전화예약 해주세요
          </div>
          <div className="notice-text">
            031-636-3360
          </div>
        </FixedNoticeItem>
      </FixedNoticeContainer>

      {isAdmin && (
        <AdminControls>
          <AdminButton onClick={openAddModal}>
            <FaPlus />
            공지사항 작성
          </AdminButton>
        </AdminControls>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>공지사항을 불러오는 중...</p>
        </div>
      )}

      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          backgroundColor: '#ffebee', 
          color: '#c62828',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <p>{error}</p>
          <small>오프라인 상태에서는 기본 공지사항만 표시됩니다.</small>
        </div>
      )}

      <NoticeList>
        {notices.map((notice) => (
          <NoticeItem
            key={notice.id}
            important={notice.important}
            special={notice.special}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: notice.id * 0.1 }}
          >
            {isAdmin && (
              <ItemControls>
                <ControlButton onClick={() => openEditModal(notice)}>
                  <FaEdit />
                </ControlButton>
                <ControlButton danger onClick={() => handleDelete(notice.id)}>
                  <FaTrash />
                </ControlButton>
              </ItemControls>
            )}
            
            <h3>
              {notice.title}
            </h3>
            <div className="date">
              <FaCalendarAlt />
              {notice.date}
            </div>
            {notice.image && (
              <div style={{ marginBottom: '15px' }}>
                <img 
                  src={notice.image} 
                  alt={notice.title}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            )}
            <p>{notice.content}</p>
          </NoticeItem>
        ))}
      </NoticeList>

      {showModal && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>
              <FaTimes />
            </CloseButton>
            <h3>{editingNotice ? '공지사항 수정' : '공지사항 작성'}</h3>
            <NoticeForm onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="제목"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
              <textarea
                placeholder="내용"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                required
              />
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="important"
                  checked={formData.important}
                  onChange={(e) => setFormData({...formData, important: e.target.checked})}
                />
                <label htmlFor="important">중요 공지사항</label>
              </div>
              <div className="image-upload-section">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <label htmlFor="image" className="upload-button">
                  <FaImage />
                  이미지 업로드
                </label>
              </div>
              {formData.imagePreview && (
                <div className="image-preview">
                  <img src={formData.imagePreview} alt="Preview" />
                  <button type="button" className="remove-image" onClick={removeImage}>
                    ×
                  </button>
                </div>
              )}
              <button type="submit">
                {editingNotice ? '수정하기' : '작성하기'}
              </button>
            </NoticeForm>
          </ModalContent>
        </Modal>
      )}
    </NoticeContainer>
  );
};

export default Notice; 